import { createApiClient } from '../../../api/apiClient';
import { DLONotificationResponse } from '../types/notification.types';
import { logger } from '../../../utils/logger';
import { handleNHOErrorWithFriendlyMessage, NHO_API_NAMES } from '../../../store/utils/nhoErrorHandler';

const api = createApiClient();

/**
 * Fetch notifications for a user
 * @param userGpid The user's GPID
 * @returns Promise with notification response
 */
export const fetchNotifications = async (
  userGpid: string
): Promise<DLONotificationResponse> => {
  try {
    logger.log(`Fetching notifications for user GPID: ${userGpid}`);
    const response = await api.get(
      `/action-plans/notifications?user_gpid=${userGpid}`
    );
    logger.log(`Notifications fetched successfully:`, response.data);
    return response.data;
  } catch (error) {
    logger.error('Error fetching notifications:', error);
    try {
      handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_NOTIFICATIONS, error, true, { userGpid });
    } catch (e) {
      console.error('Error while dispatching NHO centralized handler for notifications:', e);
    }
    throw error;
  }
};

/**
 * Mark a notification as read
 * @param notificationId The notification ID to mark as read
 * @param userGpid The user's GPID
 * @returns Promise with updated notification
 */
export const markNotificationAsRead = async (
  notificationId: number,
  userGpid: string
): Promise<any> => {
  try {
    logger.log(`Marking notification ${notificationId} as read for user ${userGpid}`);
    const response = await api.put(
      `/action-plans/notifications/${notificationId}/read`,
      { user_gpid: userGpid }
    );
    logger.log(`Notification marked as read successfully:`, response.data);
    return response.data;
  } catch (error) {
    logger.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 * @param userGpid The user's GPID
 * @returns Promise with result
 */
export const markAllNotificationsAsRead = async (
  userGpid: string
): Promise<any> => {
  try {
    logger.log(`Marking all notifications as read for user GPID: ${userGpid}`);
    const response = await api.put(
      `/action-plans/notifications/mark-all-read`,
      { user_gpid: userGpid }
    );
    logger.log(`All notifications marked as read successfully:`, response.data);
    return response.data;
  } catch (error) {
    logger.error('Error marking all notifications as read:', error);
    throw error;
  }
};
